import React, { useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { IMaskInput } from 'react-imask';
import IMask from 'imask/esm/imask';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

import * as Icon from 'components/Icon';
import useOnClickOutside from 'helpers/useClickOutside';
import { FIELD_DATE_DIRECTION } from 'modules/process/constants';

import Field from '../Field';

import classes from '../Field.module.scss';

interface IProps {
  name: string;
  label: string;
  placeholder?: string;
  showError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  readOnly?: boolean;
  direction?: FIELD_DATE_DIRECTION | null | '';

  [key: string]: any;
}

const MINUTE_STEP = 5;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP);
const DATE_FORMAT_REGEXP = /^\d{2}\.\d{2}\.\d{4}$/;

const pad = (n: number) => String(n).padStart(2, '0');

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const parseDateValue = (value?: string) => {
  if (!value || !DATE_FORMAT_REGEXP.test(value)) return null;

  const [day, month, year] = value.split('.').map(Number);
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

const getFieldKey = (name?: string) => (name || '').split('.')[0];

const getDateKeyFromTimeKey = (timeKey: string) => {
  if (/_TIME$/.test(timeKey)) return timeKey.replace(/_TIME$/, '_DATE');
  if (/_TIME_/.test(timeKey)) return timeKey.replace('_TIME_', '_DATE_');
  if (/^TIME_/.test(timeKey)) return timeKey.replace(/^TIME_/, 'DATE_');
  return '';
};

const getPairedDate = (values, name?: string) => {
  const dateKey = getDateKeyFromTimeKey(getFieldKey(name));
  return dateKey
    ? parseDateValue(values?.[dateKey]?.value) || parseDateValue(values?.parentValues?.[dateKey]?.value)
    : null;
};

const isHourAllowed = (hour: number, direction?: FIELD_DATE_DIRECTION | null | '') => {
  if (!direction) return true;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (direction === FIELD_DATE_DIRECTION.BEFORE) return hour * 60 < nowMinutes;
  if (direction === FIELD_DATE_DIRECTION.AFTER) return hour * 60 + (60 - MINUTE_STEP) >= nowMinutes;
  return true;
};

const isMinuteAllowed = (hour: number, minute: number, direction?: FIELD_DATE_DIRECTION | null | '') => {
  if (!direction) return true;
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const value = hour * 60 + minute;
  if (direction === FIELD_DATE_DIRECTION.BEFORE) return value < nowMinutes;
  if (direction === FIELD_DATE_DIRECTION.AFTER) return value > nowMinutes;
  return true;
};

const TimeField: React.FC<IProps> = ({ label, placeholder, showError = true, disabled, readOnly, autoFocus, required, direction, ...props }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState<'hour' | 'minute'>('hour');
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const ref = useRef(null);
  useOnClickOutside(ref, () => {
    setOpen(false);
    setLevel('hour');
  });

  const [field, meta, helpers] = useField(props);
  const { values } = useFormikContext();
  const hasError = !!(showError && meta.touched && meta.error);
  const pairedDate = getPairedDate(values, props.name);
  const timeDirection =
    direction === FIELD_DATE_DIRECTION.AFTER && pairedDate && pairedDate > getToday()
      ? ''
      : direction;

  const openPicker = () => {
    setLevel('hour');
    setSelectedHour(null);
    setOpen(prev => !prev);
  };

  const pickHour = (hour: number) => {
    if (!isHourAllowed(hour, timeDirection)) return;
    setSelectedHour(hour);
    setLevel('minute');
  };

  const pickMinute = (minute: number) => {
    if (selectedHour === null || !isMinuteAllowed(selectedHour, minute, timeDirection)) return;
    helpers.setValue(`${pad(selectedHour)}:${pad(minute)}`);
    setOpen(false);
    setLevel('hour');
  };

  return (
    <Field {...{ name: field.name, label, hasError, showError, error: meta.error, autoFocus, disabled, required, readOnly }} icon={
      <div className={classes.calendar}>
        <div onClick={openPicker}>
          <Icon.System.Time width="20" height="20" />
        </div>
        <Transition in={open} timeout={{ enter: 0, exit: 200 }} unmountOnExit mountOnEnter>
          {(state: TransitionStatus) => (
            <div style={{ ...transition[state] }} ref={ref} className={classes.calendarContainer}>
              <div className={cx(classes.calendarBase, classes.timeList)}>
                {level === 'hour' ? (
                  <div className={classes.timeListGrid}>
                    {HOURS.map(hour => (
                      <div
                        key={hour}
                        className={cx(classes.timeListItem, !isHourAllowed(hour, timeDirection) && classes['timeListItem--disabled'])}
                        onClick={() => pickHour(hour)}
                      >
                        {pad(hour)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className={classes.timeListHeader} onClick={() => setLevel('hour')}>
                      <Icon.System.ChevronLeft /> {pad(selectedHour as number)}
                    </div>
                    <div className={classes.timeListGrid}>
                      {MINUTES.map(minute => (
                        <div
                          key={minute}
                          className={cx(classes.timeListItem, !isMinuteAllowed(selectedHour as number, minute, timeDirection) && classes['timeListItem--disabled'])}
                          onClick={() => pickMinute(minute)}
                        >
                          {pad(selectedHour as number)}:{pad(minute)}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </Transition>
      </div>
    }>
      {({ onFocus, onBlur }) => (
        <IMaskInput
          {...{ placeholder, disabled, onFocus, readOnly }}
          id={field.name}
          name={field.name}
          value={field.value}
          type="tel"
          onBlur={(e) => {
            field.onBlur(e);
            onBlur();
          }}
          onAccept={value => helpers.setValue(value)}
          className={classes.input}
          placeholderChar=" "
          overwrite
          lazy={false}
          autofix
          mask="H:i"
          blocks={{
            H: { mask: IMask.MaskedRange, placeholderChar: t('hour_placeholder'), from: 0, to: 23, maxLength: 2 },
            i: { mask: IMask.MaskedRange, placeholderChar: t('minute_placeholder'), from: 0, to: 59, maxLength: 2 }
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      )}
    </Field>
  );
};

const transition = {
  entering: { opacity: 0, visibility: 'hidden' },
  entered: { opacity: 1, visibility: 'visible', transition: 'all .2s ease-in-out' },
  exited: { opacity: 0, visibility: 'hidden' }
};

export default TimeField;
